import { Mic, MicOff, Minimize2, Monitor, Phone, PhoneOff, Smartphone } from 'lucide-react'
import { Button } from 'tailwind-v4/components/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'tailwind-v4/components/dialog'
import { Field, FieldDescription, FieldLabel } from 'tailwind-v4/components/field'
import { Input } from 'tailwind-v4/components/input'
import { Spinner } from 'tailwind-v4/components/spinner'
import { cn } from 'tailwind-v4/lib/utils'
import { useCallSession, type CallMode } from './CallSessionProvider'

/**
 * The call, as a dialog over the session. Closing it while the call is up
 * only minimises it to the pill; Hang up is the one way to end a call.
 */
export function CallDialog() {
  const s = useCallSession()
  const t = s.target
  if (!t) return null
  const number = s.number
  const callerId = number?.caller_id_e164 ?? number?.e164 ?? null
  const recorded = number?.recording_enabled ?? false
  const canClose = !s.busy
  return (
    <Dialog open={s.dialogOpen} onOpenChange={(o) => (!o ? (canClose ? s.closeDialog() : s.minimise()) : s.showDialog())}>
      <DialogContent className="new-ui-theme sm:max-w-md">
        <DialogHeader className="pr-8">
          <DialogTitle>Call {t.enquiry.name}</DialogTitle>
          <DialogDescription className="tabular-nums">{t.enquiry.phone}</DialogDescription>
        </DialogHeader>

        {s.numbersLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : !number ? (
          <p className="text-sm text-muted-foreground">This venue has no voice number to call from. Provision one under Voice AI first.</p>
        ) : !number.outbound_enabled ? (
          <p className="text-sm text-muted-foreground">Outbound calls are switched off for this venue&apos;s number. Turn them on under Voice AI.</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2">
              <ModeButton active={s.mode === 'ring_me'} disabled={s.busy} onClick={() => s.setMode('ring_me')} icon={<Smartphone />} title="Ring my phone" hint="We ring you, then the guest" />
              <ModeButton active={s.mode === 'browser'} disabled={s.busy} onClick={() => s.setMode('browser')} icon={<Monitor />} title="From this browser" hint="Headset or laptop mic" />
            </div>
            {s.mode === 'ring_me' ? (
              <Field>
                <FieldLabel htmlFor="ring-me-number">Ring me on</FieldLabel>
                <Input id="ring-me-number" placeholder="07…" value={s.staffNumber} disabled={s.busy} onChange={(e: React.ChangeEvent<HTMLInputElement>) => s.setStaffNumber(e.target.value)} />
                <FieldDescription>Your phone rings first, showing the venue&apos;s number. Answer it and we dial the guest.</FieldDescription>
              </Field>
            ) : null}
            <p className="text-xs text-muted-foreground">
              The guest sees <span className="tabular-nums text-foreground">{callerId}</span>. {recorded ? 'The call is recorded and the guest hears a notice before you are connected.' : 'The call is not recorded.'}
            </p>
            {s.status ? <div className={cn('rounded-lg border border-border px-3 py-2 text-sm', s.softphoneState === 'failed' ? 'text-destructive' : '')}>{s.status}</div> : null}
            {s.busy ? <p className="text-xs text-muted-foreground">Minimise to keep talking while you move around; the call stays up until you hang up.</p> : null}
          </div>
        )}

        <DialogFooter className="items-center">
          {s.mode === 'browser' && s.softphoneState === 'live' ? (
            <Button variant="ghost" size="icon-sm" aria-label={s.softphone.muted ? 'Unmute' : 'Mute'} onClick={s.softphone.toggleMute}>
              {s.softphone.muted ? <MicOff className="size-4" /> : <Mic className="size-4" />}
            </Button>
          ) : null}
          {s.busy ? (
            <Button variant="outline" leftIcon={<Minimize2 />} onClick={s.minimise}>
              Minimise
            </Button>
          ) : (
            <Button variant="outline" onClick={s.closeDialog}>
              Close
            </Button>
          )}
          {s.busy ? (
            <Button variant="destructive" leftIcon={s.placing || s.hangingUp ? <Spinner /> : <PhoneOff />} onClick={() => void s.hangUp()} disabled={s.placing || s.hangingUp}>
              Hang up
            </Button>
          ) : (
            <Button variant="primary" leftIcon={<Phone />} onClick={() => void s.start()} disabled={!number?.outbound_enabled}>
              {s.ringMeOver || s.softphoneState === 'failed' ? 'Call again' : 'Call'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ModeButton({ active, disabled, onClick, icon, title, hint }: { active: boolean; disabled?: boolean; onClick: () => void; icon: React.ReactNode; title: string; hint: string }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} aria-pressed={active} className={cn('flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors disabled:opacity-60', active ? 'border-foreground bg-muted' : 'border-border hover:bg-muted/50')}>
      <span className="flex items-center gap-2 text-sm font-medium [&_svg]:size-4">
        {icon}
        {title}
      </span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </button>
  )
}

export type { CallMode }
